import React, { useState, useEffect } from "react";
import { FaBuilding, FaInstagram, FaPlus, FaTrash } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { fetchCampaigns } from "../../../Redux/campaignSlice";
import { toast } from "react-toastify";
import { getBrandsByCompany } from "../../services/companyAuthService";

const ProfileDetails = ({ data, setData }) => {
  const dispatch = useDispatch();
  const { campaigns, loading } = useSelector((state) => state.campaign);
  const [type, setType] = useState(data.affiliateType || "influencer");
  const [selectedCampaigns, setSelectedCampaigns] = useState(data.campaigns || []);
  const [showCampaignDropdown, setShowCampaignDropdown] = useState(false);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState("");
  const [loadingBrands, setLoadingBrands] = useState(false);

  useEffect(() => {
    dispatch(fetchCampaigns());
    fetchBrands();
  }, [dispatch]);

  // Set default affiliate type to "influencer" if none is selected
  useEffect(() => {
    if (!data.affiliateType) {
      setData({ ...data, affiliateType: "influencer" });
    }
  }, [data.affiliateType, data, setData]);

  const fetchBrands = async () => {
    try {
      setLoadingBrands(true);
      const response = await getBrandsByCompany();
      if (response.success) {
        setBrands(response.brands);
      }
    } catch (error) {
      console.error("Error fetching brands:", error);
      toast.error("Failed to fetch brands");
    } finally {
      setLoadingBrands(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  const handleTypeChange = (e) => {
    const value = e.target.value;

    // Only allow "influencer" type to be selected
    if (value !== "influencer") {
      toast.warning("Only Social Media Influencer option is currently available");
      return;
    }

    setType(value);
    setData({ ...data, affiliateType: value });
  };

  const handleAddCampaign = (campaign) => {
    // Check if the campaign is already selected
    if (selectedCampaigns.some(c => c._id === campaign._id)) {
      toast.warning("Campaign already selected");
      return;
    }

    // Ensure all selected campaigns are from the same brand
    if (selectedCampaigns.length > 0) {
      const firstBrandId = selectedCampaigns[0].brandId._id; // Extract brandId from the first selected campaign
      if (campaign.brandId._id !== firstBrandId) {  // Check if the brandId of the new campaign matches the selected ones
        toast.error("All campaigns must be from the same brand");
        return;
      }
    }

    // Add the campaign to the selected campaigns list
    const updatedCampaigns = [...selectedCampaigns, campaign];

    // Update the state with the new list of selected campaigns
    setSelectedCampaigns(updatedCampaigns);
    setData({
      ...data,
      campaigns: updatedCampaigns
    });

    // Close the campaign dropdown
    setShowCampaignDropdown(false);
  };

  const handleRemoveCampaign = (campaignId) => {
    const updatedCampaigns = selectedCampaigns.filter(c => c._id !== campaignId);
    setSelectedCampaigns(updatedCampaigns);
    setData({ ...data, campaigns: updatedCampaigns });
  };

  // Filter campaigns based on selected brand
  const availableCampaigns = campaigns.filter((campaign) => {
    const brandId = campaign.brandId?._id || campaign.brandId;
    return (
      campaign.status === "active" &&
      !selectedCampaigns.some((c) => c._id === campaign._id) &&
      (!selectedBrand || brandId.toString() === selectedBrand)
    );
  });

  const handleBrandChange = (e) => {
    setSelectedBrand(e.target.value);
    setShowCampaignDropdown(false); // Close campaign dropdown when brand changes
  };

  return (
    <div className="w-full bg-white shadow p-4 md:p-6 rounded-md">
      <h3 className="text-lg font-semibold mb-4">Profile Details</h3>
      <form className="space-y-6">

        {/* Radio Button Group for Affiliate Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Choose Affiliate Type <span className="text-red-500">*</span>
          </label>
          {/* <p className="text-sm text-gray-600 mb-3">
            Currently, only Social Media Influencer option is available. Other options will be enabled soon.
          </p> */}
          <div className="flex flex-wrap gap-4">
            {["individual", "influencer", "company"].map((option) => {
              const isDisabled = option !== "influencer";
              const isChecked = type === option;

              return (
                <label
                  key={option}
                  className={`flex items-center gap-2 cursor-pointer ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="radio"
                    name="affiliateType"
                    value={option}
                    checked={isChecked}
                    onChange={handleTypeChange}
                    disabled={isDisabled}
                    className={`form-radio focus:ring-orange-500 ${isDisabled ? 'text-gray-400' : 'text-orange-500'}`}
                    required
                  />
                  <span className={isDisabled ? 'text-gray-500' : 'text-gray-700'}>
                    {option === "influencer" && "Social Media Influencer"}
                    {option === "individual" && "Individual"}
                    {option === "company" && "Company"}
                  </span>
                  {isDisabled && (
                    <span className="text-xs text-gray-400 ml-1">(Coming Soon)</span>
                  )}
                </label>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="firstName"
              value={data.firstName || ""}
              onChange={handleChange}
              className="w-full border border-orange-300 rounded-md p-2 mt-1 focus:border-orange-500 focus:ring-orange-500"
              placeholder="Enter first name"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="lastName"
              value={data.lastName || ""}
              onChange={handleChange}
              className="w-full border border-orange-300 rounded-md p-2 mt-1 focus:border-orange-500 focus:ring-orange-500"
              placeholder="Enter last name"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            name="email"
            value={data.email || ""}
            onChange={handleChange}
            className="w-full border border-orange-300 rounded-md p-2 mt-1 focus:border-orange-500 focus:ring-orange-500"
            placeholder="Enter email"
            required
          />
        </div>

        {/* Conditional Fields based on selected type */}
        {type === "company" && (
          <div>
            <label className="block text-sm font-medium text-gray-700">
              <div className="flex items-center gap-2">
                <FaBuilding className="text-gray-400" />
                Company Name <span className="text-red-500">*</span>
              </div>
            </label>
            <input
              type="text"
              name="companyName"
              value={data.companyName || ""}
              onChange={handleChange}
              className="w-full border border-orange-300 rounded-md p-2 mt-1 focus:border-orange-500 focus:ring-orange-500"
              placeholder="Enter company name"
              required
            />
          </div>
        )}

        {(type === "influencer" || type === "individual") && (
          <div>
            <label className="block text-sm font-medium text-gray-700">
              <div className="flex items-center gap-2">
                <FaInstagram className="text-pink-500" />
                Instagram ID <span className="text-red-500">*</span>
              </div>
            </label>
            <input
              type="text"
              name="instagramId"
              value={data.instagramId || ""}
              onChange={handleChange}
              className="w-full border border-orange-300 rounded-md p-2 mt-1 focus:border-orange-500 focus:ring-orange-500"
              placeholder="Enter Instagram handle"
              required
            />
          </div>
        )}

        {/* Brand Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Brand
          </label>
          <select
            value={selectedBrand}
            onChange={handleBrandChange}
            className="w-full border border-orange-300 rounded-md p-2 focus:border-orange-500 focus:ring-orange-500"
          >
            <option value="">All Brands</option>
            {loadingBrands ? (
              <option disabled>Loading brands...</option>
            ) : (
              brands
                .filter(brand => brand.isActive && brand.status === 'active')
                .map(brand => (
                  <option key={brand._id} value={brand._id}>
                    {brand.brandName}
                  </option>
                ))
            )}
          </select>
        </div>

        {/* Campaign Selection Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Associated Campaigns
          </label>
          <div className="space-y-4">
            {/* Selected Campaigns */}
            {selectedCampaigns.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedCampaigns.map(campaign => (
                  <div key={campaign._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <h4 className="font-medium">{campaign.name}</h4>
                      <p className="text-sm text-gray-500">{campaign.brandId?.brandName}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveCampaign(campaign._id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Campaign Dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowCampaignDropdown(!showCampaignDropdown)}
                className="w-full flex items-center justify-between p-2 border border-orange-300 rounded-md hover:border-orange-500"
                disabled={!selectedBrand}
              >
                <span>{selectedBrand ? "Add Campaign" : "Select a brand first"}</span>
                <FaPlus className="text-orange-500" />
              </button>

              {/* {showCampaignDropdown && (
                <div className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {loading ? (
                    <div className="p-2 text-center text-gray-500">Loading campaigns...</div>
                  ) : availableCampaigns.length === 0 ? (
                    <div className="p-2 text-center text-gray-500">No available campaigns for selected brand</div>
                  ) : (
                    availableCampaigns.map(campaign => (
                      <div
                        key={campaign._id}
                        onClick={() => handleAddCampaign(campaign)}
                        className="p-2 hover:bg-gray-100 cursor-pointer"
                      >
                        <h4 className="font-medium">{campaign.name}</h4>
                        <p className="text-sm text-gray-500">{campaign.description}</p>
                      </div>
                    ))
                  )}
                </div>
              )} */}
              {showCampaignDropdown && (
                <div className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {loading ? (
                    <div className="p-2 text-center text-gray-500">
                      Loading campaigns...
                    </div>
                  ) : availableCampaigns.length === 0 ? (
                    <div className="p-4 text-center">
                      <p className="text-gray-500 mb-3">
                        No available campaigns for selected brand
                      </p>

                      <a
                        href="/company/dashboard/campaigns/create"
                        className="inline-block px-4 py-2 bg-orange-500 text-white rounded-md text-sm font-medium hover:bg-orange-600 transition"
                      >
                        + Add New Campaign
                      </a>
                    </div>
                  ) : (
                    availableCampaigns.map((campaign) => (
                      <div
                        key={campaign._id}
                        onClick={() => handleAddCampaign(campaign)}
                        className="p-2 hover:bg-gray-100 cursor-pointer"
                      >
                        <h4 className="font-medium">{campaign.name}</h4>
                        <p className="text-sm text-gray-500">
                          {campaign.description}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProfileDetails;
